import React, { useEffect, useState, useCallback, useRef } from "react";
import { getFindability, generateFindability, regenerateFindability, getFindabilityJob, saveFindabilityNotes } from "@/api";

const QUESTIONS = [
  { key: "q1", text: "Did it name you or your work specifically?" },
  { key: "q2", text: "Did it use the specific words or approach only you use — your method, your process, your language?" },
  { key: "q3", text: "Or did it default to generic advice for the broader category?" },
  { key: "q4", text: "Did it name other specific people, brands, or tools as the go-to answer?" },
];

const OUTCOMES = {
  occupied: {
    key: "occupied",
    label: "Occupied Ocean",
    swatch: "🟦",
    text: "The category exists, and it's crowded. AI already has go-to answers for this — they're just not you yet. Your work across your focused efforts is differentiation: what's the specific angle only you bring?",
  },
  open: {
    key: "open",
    label: "Open Water",
    swatch: "🟨",
    text: "The category is recognized, but no one's claimed the answer yet. This is exactly what your Findability Cycle is built for — showing up consistently is the whole game here.",
  },
  uncharted: {
    key: "uncharted",
    label: "Uncharted",
    swatch: "⬛",
    text: "This is the rarest result. AI isn't just missing you — it doesn't have a name for what you do yet. Your work isn't just showing up more. It's teaching AI what the category even is.",
  },
};

// Outcome is driven by Q3 (generic category advice) + Q4 (competitors named).
function computeOutcome(answers) {
  if (!answers || QUESTIONS.some((q) => !answers[q.key])) return null;
  if (answers.q4 === "yes") return "occupied";
  if (answers.q3 === "yes") return "open";
  return "uncharted";
}

function CompareBanner({ day0Outcome, day30Outcome }) {
  if (!day0Outcome || !day30Outcome) return null;
  const o0 = OUTCOMES[day0Outcome];
  const o30 = OUTCOMES[day30Outcome];
  const message =
    day0Outcome === day30Outcome
      ? `Holding at ${o0.label} — consistency is doing its work. Keep showing up.`
      : `You moved from ${o0.label} to ${o30.label}. That shift is what showing up creates.`;
  return (
    <div className="fc-compare" data-testid="findability-compare">
      <span className="fc-compare-label">Your Findability shift</span>
      <div className="fc-compare-row">
        <span className={`fc-chip fc-chip--${day0Outcome}`}>
          <span aria-hidden="true">{o0.swatch}</span> {o0.label}
        </span>
        <span className="fc-arrow" aria-hidden="true">→</span>
        <span className={`fc-chip fc-chip--${day30Outcome}`}>
          <span aria-hidden="true">{o30.swatch}</span> {o30.label}
        </span>
      </div>
      <div className="fc-compare-text">{message}</div>
    </div>
  );
}

function SelfCheck({ dayKey, answers, onChange }) {
  const outcome = computeOutcome(answers);
  return (
    <div className="fc-selfcheck" data-testid={`findability-selfcheck-${dayKey}`}>
      <div className="fc-teach" data-testid={`findability-teach-${dayKey}`}>
        “Sometimes the answer that comes back sounds like it's describing anyone, not you. That's not always a
        sign you're behind. Look closely at what's actually missing.”
      </div>
      <div className="fc-questions" role="group" aria-label={`${dayKey === "day0" ? "Baseline" : "Progress"} self-check questions`}>
        {QUESTIONS.map((q) => (
          <div className="fc-question" key={q.key} data-testid={`findability-${dayKey}-${q.key}`}>
            <div className="fc-q-text">{q.text}</div>
            <div className="fc-q-toggle">
              {["yes", "no"].map((val) => {
                const selected = answers[q.key] === val;
                return (
                  <button
                    key={val}
                    type="button"
                    className={`fc-yn${selected ? " selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => onChange(q.key, val)}
                    data-testid={`findability-${dayKey}-${q.key}-${val}`}
                  >
                    {val === "yes" ? "Yes" : "No"}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {outcome && (
        <div
          className={`fc-outcome fc-outcome--${outcome}`}
          role="status"
          data-testid={`findability-outcome-${dayKey}`}
          data-outcome={outcome}
        >
          <div className="fc-outcome-title">
            <span aria-hidden="true">{OUTCOMES[outcome].swatch}</span> {OUTCOMES[outcome].label}
          </div>
          <div className="fc-outcome-text">{OUTCOMES[outcome].text}</div>
          <div className="fc-outcome-hint">Your result reflects the last two questions. The first two are for your own reflection as your visibility grows.</div>
        </div>
      )}
    </div>
  );
}

export default function FindabilityCheck({ hasTitle }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ phrases: [], generated: false });
  const [day0, setDay0] = useState("");
  const [day30, setDay30] = useState("");
  const [day0Answers, setDay0Answers] = useState({});
  const [day30Answers, setDay30Answers] = useState({});
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const [savedFlash, setSavedFlash] = useState(false);
  const [queueMsg, setQueueMsg] = useState("");
  const firstSave = useRef(true);
  const pollRef = useRef(null);
  const submittingRef = useRef(false);

  const hydrate = (d) => {
    setData(d);
    setDay0(d.day0_note || "");
    setDay30(d.day30_note || "");
    setDay0Answers(d.day0_answers || {});
    setDay30Answers(d.day30_answers || {});
  };

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  useEffect(() => {
    (async () => {
      try {
        hydrate(await getFindability());
      } catch (e) {
        console.error("Failed to load findability check:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Debounced autosave of notes + self-check answers once phrases exist.
  useEffect(() => {
    if (firstSave.current) { firstSave.current = false; return; }
    if (!data.generated) return;
    const t = setTimeout(() => {
      saveFindabilityNotes({ day0_note: day0, day30_note: day30, day0_answers: day0Answers, day30_answers: day30Answers })
        .then(() => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1500); })
        .catch((e) => console.error("Failed to save findability notes:", e));
    }, 700);
    return () => clearTimeout(t);
  }, [day0, day30, day0Answers, day30Answers, data.generated]);

  const finishJob = useCallback((res) => {
    // Returns true when the job reached a terminal state.
    if (res.status === "done") { hydrate(res); setGenerating(false); setQueueMsg(""); submittingRef.current = false; return true; }
    if (res.status === "failed" || res.status === "busy") {
      setError(res.message || "The studio is extra busy right now. Your work is saved — try again in a few minutes.");
      setGenerating(false); setQueueMsg(""); submittingRef.current = false;
      return true;
    }
    if (res.status === "queued" && res.position > 1) {
      setQueueMsg(`You're #${res.position} in line — hang tight, your phrases are moments away.`);
    } else {
      setQueueMsg("Finding your phrases…");
    }
    return false;
  }, []);

  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 70; // ~90s ceiling before graceful failure
    const tick = async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        setError("The studio is extra busy right now. Your work is saved — try again in a few minutes.");
        setGenerating(false); setQueueMsg(""); submittingRef.current = false;
        return;
      }
      try {
        const res = await getFindabilityJob(jobId);
        if (!finishJob(res)) pollRef.current = setTimeout(tick, 1300);
      } catch (e) {
        setError("The studio is extra busy right now. Your work is saved — try again in a few minutes.");
        setGenerating(false); setQueueMsg(""); submittingRef.current = false;
      }
    };
    pollRef.current = setTimeout(tick, 900);
  }, [finishJob]);

  const startJob = useCallback((res) => {
    if (!finishJob(res) && res.job_id) pollJob(res.job_id);
  }, [finishJob, pollJob]);

  const doGenerate = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setGenerating(true);
    setError("");
    setQueueMsg("Finding your phrases…");
    try {
      startJob(await generateFindability());
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate phrases. Please try again.");
      setGenerating(false); setQueueMsg(""); submittingRef.current = false;
    }
  }, [startJob]);

  const doRegenerate = useCallback(async () => {
    if (submittingRef.current) return;
    if (!window.confirm("Regenerate your search phrases from your current profile? This replaces the current phrases. Do this if your Searchable Title changed or the phrases were built from old info — your baseline / end-of-cycle notes are kept.")) return;
    submittingRef.current = true;
    setGenerating(true);
    setError("");
    setQueueMsg("Finding your phrases…");
    try {
      startJob(await regenerateFindability());
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not regenerate phrases. Please try again.");
      setGenerating(false); setQueueMsg(""); submittingRef.current = false;
    }
  }, [startJob]);

  const copyPhrase = (text, idx) => {
    navigator.clipboard?.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(-1), 1600);
  };

  const setAnswer = (dayKey) => (qKey, val) => {
    const setter = dayKey === "day0" ? setDay0Answers : setDay30Answers;
    setter((prev) => ({ ...prev, [qKey]: val }));
  };

  if (loading) {
    return (
      <div className="tab-pane" id="tab-findability" role="tabpanel" aria-labelledby="tabbtn-findability">
        <div className="gen-anim"><div className="gen-dots"><span /><span /><span /></div>Loading your self-check…</div>
      </div>
    );
  }

  return (
    <div className="tab-pane" id="tab-findability" role="tabpanel" aria-labelledby="tabbtn-findability">
      <h2 className="section-h2">Test Your Findability</h2>
      <p className="section-sub">
        A simple before &amp; after. Test real search phrases yourself in ChatGPT or Perplexity — once now, once at the end of your cycle — and see how your visibility changes.
      </p>

      {!data.generated ? (
        <div className="fc-generate-card" data-testid="findability-generate-card">
          <div className="fc-lead">
            We'll build 2-3 real search phrases from your <strong>Searchable Title</strong> and <strong>who you help</strong> —
            written the way someone looking for you would actually type them.
          </div>
          {!hasTitle && (
            <div className="fc-warn" data-testid="findability-no-title">
              Add your <strong>Searchable Title</strong> on the Generate tab first, then come back here.
            </div>
          )}
          <button
            className="btn btn-teal"
            onClick={doGenerate}
            disabled={generating || !hasTitle}
            data-testid="findability-generate-button"
          >
            {generating ? "Building your phrases…" : "🔎 Generate my search phrases"}
          </button>
          {generating && queueMsg && (
            <div className="fc-queue" data-testid="findability-queue-status">{queueMsg}</div>
          )}
          {error && <div className="fc-error" data-testid="findability-error">{error}</div>}
        </div>
      ) : (
        <>
          <CompareBanner
            day0Outcome={computeOutcome(day0Answers)}
            day30Outcome={computeOutcome(day30Answers)}
          />
          <div className="fc-instructions" data-testid="findability-instructions">
            Paste each phrase into <strong>ChatGPT</strong> or <strong>Perplexity</strong> in an{" "}
            <strong>incognito or private window, logged out of your account</strong>. This shows what a
            stranger would actually see — not what AI remembers about you personally. See if you show up,
            and note what you find below — no wrong answers, this is just your starting point.
          </div>

          <div className="fc-phrases" data-testid="findability-phrases">
            {data.phrases.map((p, i) => (
              <div className="fc-phrase" key={`phrase-${i}`} data-testid={`findability-phrase-${i}`}>
                <span className="fc-phrase-text">“{p}”</span>
                <button className="btn-tiny" onClick={() => copyPhrase(p, i)} data-testid={`findability-copy-${i}`}>
                  {copiedIdx === i ? "Copied!" : "Copy"}
                </button>
              </div>
            ))}
            <button
              className="fc-regen"
              onClick={doRegenerate}
              disabled={generating}
              data-testid="findability-regenerate-button"
            >
              {generating ? "Regenerating…" : "↻ Regenerate from my current profile"}
            </button>
            {generating && queueMsg && (
              <div className="fc-queue" data-testid="findability-queue-status">{queueMsg}</div>
            )}
            {error && <div className="fc-error" data-testid="findability-error">{error}</div>}
          </div>

          <div className="fc-day-block" data-testid="findability-day0-block">
            <label htmlFor="fcDay0" className="fc-note-label">Baseline — what I found</label>
            <textarea
              id="fcDay0"
              className="fc-textarea"
              placeholder="e.g., I didn't show up at all yet — mostly big publishers and generic advice."
              value={day0}
              onChange={(e) => setDay0(e.target.value)}
              data-testid="findability-day0-input"
            />
            <SelfCheck dayKey="day0" answers={day0Answers} onChange={setAnswer("day0")} />
          </div>

          <div className="fc-day-block" data-testid="findability-day30-block">
            <label htmlFor="fcDay30" className="fc-note-label">End of cycle — what I found</label>
            <textarea
              id="fcDay30"
              className="fc-textarea"
              placeholder="Come back at the end of your cycle and note what's changed."
              value={day30}
              onChange={(e) => setDay30(e.target.value)}
              data-testid="findability-day30-input"
            />
            <SelfCheck dayKey="day30" answers={day30Answers} onChange={setAnswer("day30")} />
          </div>

          <div className="fc-save-status" data-testid="findability-save-status">
            {savedFlash ? "✓ Saved" : "Your notes save automatically."}
          </div>
        </>
      )}
    </div>
  );
}
